import React from 'react';
import { X } from 'lucide-react';
import { cn } from '../../services/utils';

export interface AppSidebarProps {
  children: React.ReactNode;
  isOpen?: boolean;
  onClose?: () => void;
  className?: string;
}

export function AppSidebar({ children, isOpen = true, onClose, className }: AppSidebarProps) {
  return (
    <>
      {/* Mobile overlay */}
      {isOpen && onClose && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed top-16 left-0 z-50 h-[calc(100vh-4rem)] w-64 bg-white border-r border-gray-200',
          'transition-transform duration-300 ease-in-out overflow-y-auto',
          'lg:fixed lg:left-0 lg:top-16 lg:bottom-0 lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full',
          className
        )}
      >
        {/* Mobile close button */}
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 lg:hidden"
            aria-label="Close sidebar"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        )}

        {/* Sidebar content */}
        <div className="h-full overflow-y-auto py-6">{children}</div>
      </aside>
    </>
  );
}

export interface SidebarSectionProps {
  title?: string;
  children: React.ReactNode;
  className?: string;
}

export function SidebarSection({ title, children, className }: SidebarSectionProps) {
  return (
    <div className={cn('mb-6', className)}>
      {title && (
        <h3 className="px-4 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
          {title}
        </h3>
      )}
      <nav className="space-y-1 px-3">{children}</nav>
    </div>
  );
}

export interface SidebarItemProps {
  icon?: React.ReactNode;
  label: string;
  isActive?: boolean;
  onClick?: () => void;
  href?: string;
  disabled?: boolean;
  className?: string;
}

export function SidebarItem({
  icon,
  label,
  isActive = false,
  onClick,
  href,
  disabled = false,
  className,
}: SidebarItemProps) {
  const baseClasses = cn(
    'flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors w-full text-left',
    disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-gray-100',
    isActive && !disabled
      ? 'bg-blue-50 text-blue-600 hover:bg-blue-100'
      : 'text-gray-700 hover:text-gray-900',
    className
  );

  const content = (
    <>
      {icon && (
        <span
          className={cn('flex-shrink-0', isActive && !disabled ? 'text-blue-600' : 'text-gray-500')}
        >
          {icon}
        </span>
      )}
      <span className="flex-1 truncate">{label}</span>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        className={baseClasses}
        onClick={disabled ? (e) => e.preventDefault() : onClick}
        aria-disabled={disabled}
      >
        {content}
      </a>
    );
  }

  return (
    <button type="button" className={baseClasses} onClick={onClick} disabled={disabled}>
      {content}
    </button>
  );
}
